package org.catharsis.spendtel.feature.data

import kotlin.math.abs
import kotlin.math.round

fun formatCurrency(amount: Double, currency: String = "USD"): String {
    val sign = if (amount < 0) "-" else ""
    val absolute = abs(amount)
    val roundedCents = round(absolute * 100).toLong()
    val whole = roundedCents / 100
    val fraction = (roundedCents % 100).toInt()
    val formattedAmount = "$whole.${fraction.toString().padStart(2, '0')}"

    return when (currency.uppercase()) {
        "USD" -> "$sign$$formattedAmount"
        else -> "$sign$formattedAmount ${currency.uppercase()}"
    }
}

fun formatPeriodRange(periodStart: String?, periodEnd: String?): String? {
    if (periodStart.isNullOrBlank() && periodEnd.isNullOrBlank()) return null

    val start = periodStart?.let(::formatIsoDate).orEmpty()
    val end = periodEnd?.let(::formatIsoDate).orEmpty()

    return when {
        start.isNotBlank() && end.isNotBlank() -> "$start – $end"
        start.isNotBlank() -> start
        else -> end
    }
}

fun formatStatementPeriodLabel(periodStart: String?, periodEnd: String?): String? {
    val start = periodStart?.let(::parseIsoDate)
    val end = periodEnd?.let(::parseIsoDate)

    return when {
        start != null && end != null -> {
            when {
                start.year == end.year && start.month == end.month ->
                    "${fullMonthNames[start.month - 1]} ${start.year}"
                start.year == end.year ->
                    "${shortMonthNames[start.month - 1]} – ${shortMonthNames[end.month - 1]} ${start.year}"
                else ->
                    "${shortMonthNames[start.month - 1]} ${start.year} – " +
                        "${shortMonthNames[end.month - 1]} ${end.year}"
            }
        }
        start != null -> "${fullMonthNames[start.month - 1]} ${start.year}"
        end != null -> "${fullMonthNames[end.month - 1]} ${end.year}"
        else -> null
    }
}

fun statementPeriodLabelForReport(
    periodStart: String?,
    periodEnd: String?,
    createdAtMillis: Long,
    isProcessing: Boolean,
): String {
    return formatStatementPeriodLabel(periodStart, periodEnd)
        ?: when {
            isProcessing -> "Processing"
            else -> formatReportDate(createdAtMillis)
        }
}

fun formatIsoDate(isoDate: String): String {
    val parsed = parseIsoDate(isoDate) ?: return isoDate
    val monthName = shortMonthNames.getOrElse(parsed.month - 1) { parsed.month.toString() }
    return "$monthName ${parsed.day}, ${parsed.year}"
}

private data class ParsedIsoDate(
    val year: Int,
    val month: Int,
    val day: Int,
)

private fun parseIsoDate(isoDate: String): ParsedIsoDate? {
    val parts = isoDate.split("-")
    if (parts.size != 3) return null

    val year = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31) return null

    return ParsedIsoDate(year = year, month = month, day = day)
}

private val shortMonthNames = listOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
)

private val fullMonthNames = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
)
